/**
 * Add to Dashboard Modal Component
 * Extracted from ChatPage for reuse in ChatPageV2
 */

import React from 'react';
import {
  TextField,
  Typography,
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AppDialog } from './dialog';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BarChartIcon from '@mui/icons-material/BarChart';
import { formatRelativeTime } from '../utils/date';
import { getChartPalette } from '../theme/charts';
import { ModeContext } from '../theme/ThemeProvider';

interface AddToDashboardModalProps {
  open: boolean;
  onClose: () => void;
  dashboards: Array<{ dashboard_id: string; name: string; description?: string; created_at: string }>;
  loading: boolean;
  error: string | null;
  selectedDashboardId: string | null;
  setSelectedDashboardId: (v: string | null) => void;
  newDashboardName: string;
  setNewDashboardName: (v: string) => void;
  widgetName: string;
  setWidgetName: (v: string) => void;
  onSubmit: () => void;
  creating: boolean;
  submitError: string | null;
}

export default function AddToDashboardModal({
  open,
  onClose,
  dashboards,
  loading,
  error,
  selectedDashboardId,
  setSelectedDashboardId,
  newDashboardName,
  setNewDashboardName,
  widgetName,
  setWidgetName,
  onSubmit,
  creating,
  submitError,
}: AddToDashboardModalProps) {
  const { branding } = React.useContext(ModeContext);
  const chartPalette = React.useMemo(() => getChartPalette(branding), [branding]);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Filter dashboards based on search
  const filteredDashboards = React.useMemo(() => {
    if (!searchQuery.trim()) return dashboards;
    const query = searchQuery.toLowerCase();
    return dashboards.filter(d => d.name.toLowerCase().includes(query));
  }, [dashboards, searchQuery]);
  
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Add to dashboard"
      busy={creating}
      dividers
      primaryAction={{ label: 'Add', onClick: onSubmit }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
        <TextField
          label="Widget name"
          fullWidth
          margin="normal"
          value={widgetName}
          onChange={(e) => setWidgetName(e.target.value)}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '1rem' },
            },
          }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 2, mb: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Choose a dashboard
          </Typography>
          <TextField
            placeholder="Search dashboards..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ maxHeight: { xs: 'min(42vh, 280px)', sm: 300 }, overflow: 'hidden' }}>
          {!loading && filteredDashboards.length === 0 && !searchQuery ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No dashboards yet. Create one below.
              </Typography>
            </Box>
          ) : filteredDashboards.length === 0 && searchQuery ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                No dashboards found matching "{searchQuery}"
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gridAutoRows: 'max-content',
                gap: 1.5,
                maxHeight: { xs: 'min(42vh, 280px)', sm: 300 },
                overflowY: 'auto',
                alignContent: 'start',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {filteredDashboards.map((d, idx) => {
                const dashboardColor = chartPalette[idx % chartPalette.length];
                const isSelected = selectedDashboardId === d.dashboard_id;
                
                return (
                  <Card
                    key={d.dashboard_id}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      transition: 'all .2s ease',
                      position: 'relative',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => theme.palette.mode === 'light'
                          ? '0 4px 12px rgba(16,24,40,0.12)'
                          : '0 4px 12px rgba(0,0,0,0.4)'
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelectedDashboardId(d.dashboard_id)}
                      sx={{
                        alignItems: 'stretch',
                        height: '100%',
                        minHeight: { xs: 72, sm: 'auto' },
                      }}
                    >
                      <CardContent sx={{ py: { xs: 1.75, sm: 1.5 }, px: { xs: 1.5, sm: 2 }, pb: 1.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, mb: 1 }}>
                          <Box sx={{
                            width: { xs: 44, sm: 40 },
                            height: { xs: 44, sm: 40 },
                            bgcolor: (theme) => theme.palette.mode === 'light' ? `${dashboardColor}15` : `${dashboardColor}25`,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            mt: 0.25,
                          }}>
                            <BarChartIcon sx={{ fontSize: 20, color: dashboardColor }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              fontWeight={700}
                              sx={{
                                fontSize: '0.9375rem',
                                lineHeight: 1.3,
                                mb: 0.5,
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 2, sm: 1 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                            >
                              {d.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.8125rem',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 3, sm: 2 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                            >
                              {d.description || 'No description'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Created {formatRelativeTime(d.created_at)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Or create new
        </Typography>
        <TextField
          label="New dashboard name"
          fullWidth
          margin="normal"
          value={newDashboardName}
          onChange={(e) => setNewDashboardName(e.target.value)}
        />
        {submitError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {submitError}
          </Alert>
        )}
    </AppDialog>
  );
}
